// This is only allowed to use in the dayjsUTC helper export
// eslint-disable-next-line no-restricted-imports
import dayjs from "dayjs"
import type { Dayjs } from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import bigIntSupport from "dayjs/plugin/bigIntSupport"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import relativeTime from "dayjs/plugin/relativeTime"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(bigIntSupport)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export type LookbackUnit = "hours" | "days" | "weeks" | "months" | "years"
export type DynamicDateRange = [Dayjs | null, Dayjs | null] | RelativeLookback

export interface RelativeLookback {
  lookbackUnit: LookbackUnit
  lookbackValue: number
}

export const safelyParseDate = (date: Dayjs | string | null | undefined) => {
  if (!date) {
    return null
  }
  try {
    return dayjsUTC(date)
  } catch (_error) {
    return null
  }
}

export const isRelative = (
  dateRange: DynamicDateRange
): dateRange is RelativeLookback => {
  return (
    typeof dateRange === "object" &&
    "lookbackUnit" in dateRange &&
    "lookbackValue" in dateRange
  )
}

export const lookbackToDates = (lookback: RelativeLookback): [Dayjs, Dayjs] => {
  const now = dayjs()
  return [now.subtract(lookback.lookbackValue, lookback.lookbackUnit), now]
}

export const getDates = (
  dateRange: DynamicDateRange
): [Dayjs | null, Dayjs | null] => {
  if (isRelative(dateRange)) {
    return lookbackToDates(dateRange)
  }
  const [start, end] = dateRange
  return [safelyParseDate(start), safelyParseDate(end)]
}

export const isSameLookback = (
  a: RelativeLookback | null | undefined,
  b: RelativeLookback | null | undefined
): boolean =>
  !!a &&
  !!b &&
  a.lookbackUnit === b.lookbackUnit &&
  a.lookbackValue === b.lookbackValue /**
 * Use dayjsUTC in place of dayjs for consistency across platform
 */

export const dayjsUTC = (...args: Parameters<typeof dayjs.utc>) =>
  dayjs.utc(...args)

/** Normalize picker/API values to a calendar date string (YYYY-MM-DD). */
export const toCalendarDateString = (
  date: Dayjs | string | null | undefined
): string | null => {
  if (!date) {
    return null
  }

  if (typeof date === "string") {
    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})/.exec(date)
    if (dateOnlyMatch?.[1]) {
      return dateOnlyMatch[1]
    }
  }

  try {
    const parsed = typeof date === "string" ? dayjs(date) : date
    if (!parsed.isValid()) {
      return null
    }
    return parsed.local().format("YYYY-MM-DD")
  } catch (_error) {
    return null
  }
}

/** Format timestamps for display in the user's local timezone. */
export const formatLocalDateTime = (
  date: Dayjs | string | null | undefined,
  pattern = "MM/DD/YY h:mm A"
) => {
  const parsed = safelyParseDate(date)
  if (!parsed?.isValid()) {
    return ""
  }
  return parsed.local().format(pattern)
}

/** Format API calendar dates (YYYY-MM-DD) without local timezone shift. */
export const formatDateOnly = (
  date: Dayjs | string | null | undefined,
  pattern = "MM/DD/YY"
): string => {
  const calendarDate = toCalendarDateString(date)
  if (!calendarDate) {
    return ""
  }
  return dayjsUTC(calendarDate).format(pattern)
}
